import React, { useEffect, useRef, useState, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";

/**
 * MagicCursor — Glowing wand tip with spell spark trail.
 * Lightweight: no canvas, pure CSS. The tip is a golden orb
 * with a trailing sparkle that fades.
 */

const MagicCursor: React.FC = () => {
  const tipRef = useRef<HTMLDivElement>(null);
  const sparkRef = useRef<HTMLDivElement>(null);
  const mouse = useRef({ x: -100, y: -100 });
  const pos = useRef({ x: -100, y: -100 });
  const [clicking, setClicking] = useState(false);
  const [hovering, setHovering] = useState(false);

  const onMove = useCallback((e: MouseEvent) => {
    mouse.current = { x: e.clientX, y: e.clientY };
    if (tipRef.current) {
      tipRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    }
  }, []);

  useEffect(() => {
    let raf: number;
    const follow = () => {
      pos.current.x += (mouse.current.x - pos.current.x) * 0.15;
      pos.current.y += (mouse.current.y - pos.current.y) * 0.15;
      if (sparkRef.current) {
        sparkRef.current.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px)`;
      }
      raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mousedown", () => setClicking(true));
    document.addEventListener("mouseup", () => setClicking(false));
    const checkHover = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      setHovering(!!el.closest("a, button, [role='button'], input, textarea, select, [data-clickable]"));
    };
    document.addEventListener("mouseover", checkHover, { passive: true });
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", checkHover);
    };
  }, [onMove]);

  return (
    <>
      <SparkTrail ref={sparkRef} $hovering={hovering} $clicking={clicking} />
      <WandTip ref={tipRef} $hovering={hovering} $clicking={clicking} />
    </>
  );
};

export default MagicCursor;

const tipPulse = keyframes`
  0%, 100% { box-shadow: 0 0 6px 2px rgba(255,215,0,0.5), 0 0 12px 4px rgba(255,180,0,0.2); }
  50% { box-shadow: 0 0 10px 3px rgba(255,215,0,0.7), 0 0 20px 6px rgba(255,180,0,0.3); }
`;

const WandTip = styled.div<{ $hovering: boolean; $clicking: boolean }>`
  position: fixed;
  top: -5px;
  left: -5px;
  z-index: 99999;
  pointer-events: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #fff8dc, #ffd700 40%, #cc9900 100%);
  transform: translate(-100px, -100px);
  will-change: transform;
  animation: ${tipPulse} 1.8s ease-in-out infinite;
  transition: width 0.2s, height 0.2s, top 0.2s, left 0.2s;

  ${({ $hovering }) => $hovering && css`
    width: 14px;
    height: 14px;
    top: -7px;
    left: -7px;
    background: radial-gradient(circle at 35% 35%, #fff, #c8b0ff 40%, #8b5cf6 100%);
    box-shadow: 0 0 12px 4px rgba(139,92,246,0.5), 0 0 25px 6px rgba(139,92,246,0.2);
    animation: none;
  `}

  ${({ $clicking }) => $clicking && css`
    width: 8px;
    height: 8px;
    top: -4px;
    left: -4px;
    background: #fff;
    box-shadow: 0 0 20px 8px rgba(255,215,0,0.9), 0 0 40px 12px rgba(255,180,0,0.4);
  `}
`;

const SparkTrail = styled.div<{ $hovering: boolean; $clicking: boolean }>`
  position: fixed;
  top: -8px;
  left: -8px;
  z-index: 99998;
  pointer-events: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,215,0,0.15) 0%, transparent 70%);
  transform: translate(-100px, -100px);
  will-change: transform;
  transition: width 0.3s, height 0.3s, opacity 0.3s;

  ${({ $hovering }) => $hovering && css`
    width: 24px;
    height: 24px;
    top: -12px;
    left: -12px;
    background: radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%);
  `}
`;
